import React from 'react';
import "./Table.scss";
import GridFooter from "./GridFooter";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { GridCellParams, GridCellEditCommitParams, GridCellValue, GridColDef, GridRenderCellParams, GridRowData, GridRowParams, GridValueFormatterParams, useGridApiRef, MuiBaseEvent, GridRenderEditCellParams, renderEditInputCell } from '@mui/x-data-grid-pro';
import { DataGrid } from "../../../shared/components";
import * as _ from "lodash";
import clsx from "clsx";
import { getColumnDateTimeFormatter, getColumnNumberFormatter } from '../../../shared/utilities';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { CellWithTooltipRenderer } from "./CellWithTooltip";
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import { JsxRenderer } from '../ContentRenderer/JsxRenderer';
import { useCanvasBindings } from '../../../shared/providers/CanvasBindingsProvider';

interface ParameterMap {
    fieldName: string;
    parameterName: string;
}

interface Column extends Omit<GridColDef, 'valueFormatter'> {
    index?: number;
    valueFormatter?: (params: GridValueFormatterParams) => GridCellValue | Array<string>;
    renderTooltip?: (params: GridCellParams) => React.ReactNode;

    /**
     * @deprecated use `renderCell` instead.
    */
    cellTemplate?: string;
    /**
     * @deprecated use `renderEditCell` instead.
    */
    cellEditTemplate?: string;
    /**
     * @deprecated use `renderEditCell` instead.
    */
    cellEditTemplateFieldName?: string;
    /**
     * @deprecated use `renderTooltip` instead.
    */
    tooltipTemplate?: string;
}

interface TableProps {
    name?: string;

    rows: GridRowData[];
    columns: Column[];
    additionalColumns?: Column[];

    navigateTo?: string;
    parameterMap?: ParameterMap[];

    selectedRowParameter?: string;
    editedRowsParameter?: string;
    allRowsParameter?: string;

    onRowClick?: (param: GridRowParams) => void;
    onRowEdited?: (row: any, originalRow: any) => void | boolean;

    hideExportToExcel?: boolean;
    additionalFooterItems?: React.ReactNode;

    className?: string;
}

function mapValueFormatter(valueFormatter: any, columnType: string = "number"): (params: GridValueFormatterParams) => GridCellValue {
    if (_.isFunction(valueFormatter)) {
        return valueFormatter;
    }
    else if (_.isArray(valueFormatter)) {
        switch (columnType) {
            case "number": return getColumnNumberFormatter(...valueFormatter);
            case "date":
            case "dateTime": return getColumnDateTimeFormatter(...valueFormatter);
        }
    }
}

function mapCellClassName(cellClassName?: string | Function | Array<string>): (GridCellParams) => string {
    if (_.isFunction(cellClassName)) {
        return cellClassName;
    }
    if (_.isArray(cellClassName)) {
        return new Function(...cellClassName) as (GridCellParams) => string; // eslint-disable-line no-new-func
    }
    if (_.isString(cellClassName)) {
        return (_) => cellClassName;
    }

    return (_) => "";
}

function cellEditHighlighter(params: GridCellParams) {
    return clsx({ "is-dirty": _.find(params.row?.editedFields, f => f === params.field) });
}

function getFieldValueSetterForRow(gridApi: GridApiPro, row: any) {
    return (field: string, value: any) => {
        gridApi.setEditRowsModel({ [row.id]: { [field]: { value: value } } });
        gridApi.commitCellChange({ id: row.id, field: field }, new Event("selectionchange"));
        gridApi.setCellMode(row.id, field, "view");
    }
}

function mapColumn(column: Column): GridColDef {
    const {
        valueFormatter,
        type,
        cellTemplate,
        renderCell,
        tooltipTemplate,
        renderTooltip,
        cellClassName,
        cellEditTemplate,
        cellEditTemplateFieldName,
        renderEditCell
    } = column;

    const RenderCell =
        renderCell ? renderCell :
            cellTemplate ? ((params: GridRenderCellParams) => params.cellMode === "view" ? <JsxRenderer data={{ ...params.row, row: params.row, gridApi: params.api, setFieldValue: getFieldValueSetterForRow(params.api, params.row), params }} template={cellTemplate} /> : null) :
                null;

    const RenderEditCell =
        renderEditCell ? renderEditCell :
            cellEditTemplate ? ((params: GridRenderEditCellParams) => <JsxRenderer data={{ ...params.row, row: params.row, gridApi: params.api, params }} template={cellEditTemplate ?? ""} />) :
                cellEditTemplateFieldName ? ((params: GridRenderEditCellParams) => params?.row?.[cellEditTemplateFieldName] ? <JsxRenderer data={{ ...params.row, row: params.row, gridApi: params.api, params }} template={params?.row?.[cellEditTemplateFieldName] ?? ""} /> :  renderEditInputCell(params)) :
                    null;

    const RenderTooltip =
        renderTooltip ? ((params: GridRenderCellParams) => params.cellMode === "view" && renderTooltip(params)) :
            tooltipTemplate ? ((params: GridRenderCellParams) => params.cellMode === "view" && <JsxRenderer data={{ ...params.row, row: params.row, params }} template={tooltipTemplate} />) :
                null;

    const RenderCellWithTooltip =
        RenderTooltip ? (params: GridRenderCellParams) => <CellWithTooltipRenderer tooltipRenderer={RenderTooltip} cellRenderer={RenderCell} {...params} /> :
            null;

    const classNameHandler = mapCellClassName(cellClassName);
    const combinedCellClassName = (params: GridCellParams) => clsx(cellEditHighlighter(params), classNameHandler(params));

    let result = {
        ...column,
        valueFormatter: mapValueFormatter(valueFormatter, type),
        renderCell: RenderCellWithTooltip ?? RenderCell,
        cellClassName: combinedCellClassName
    };

    // For some reason setting `renderEditCell` to null/undefined breaks editing for all fields, so we need to omit the field altogether instead...
    if (RenderEditCell) result.renderEditCell = RenderEditCell;

    return result;
}

function Table(props: TableProps) {
    const { selectedRowParameter, parameterMap, navigateTo, rows, columns, additionalColumns, className, allRowsParameter, editedRowsParameter, onRowEdited, additionalFooterItems, ...other } = props;
    const [allColumns, setAllColumns] = React.useState(columns);
    const [currentRows, setCurrentRows] = React.useState(rows);
    const apiRef = useGridApiRef();
    const parameterValues = useSelector((store: RootState) => store.storyline.parameterValues);
    const dispatch = useThunkDispatch();
    const { navigateToPage } = useCanvasBindings();

    React.useEffect(() => {
        const result = columns?.map(c => mapColumn(c));
        _.forEach(additionalColumns, additionalColumn => {
            result.splice(additionalColumn.index, 0, mapColumn(additionalColumn));
        });
        setAllColumns(result);

    }, [columns, additionalColumns]);

    React.useEffect(() => {
        if (currentRows?.length === rows?.length && _.isMatch(currentRows, rows)) return;
        setCurrentRows(rows);

        // Clear out the edited rows parameter when the grid data is refreshed...
        if (editedRowsParameter) {
            dispatch(updateParameterValue(editedRowsParameter, []));
        }
    }, [rows]);

    const onRowClick = React.useCallback(
        (e: GridRowParams) => {
            const data = e.row;

            if (parameterMap?.length && navigateTo) {
                const parameterObject = parameterMap.reduce((acc, elem) => {
                    acc[elem.parameterName] = data[elem.fieldName];
                    return acc;
                }, {} as Record<string, any>);
                navigateToPage(navigateTo, parameterObject, true);
            }
            else if (selectedRowParameter) {
                dispatch(updateParameterValue(selectedRowParameter, data));
            }

            return () => null;
        },
        [parameterMap, updateParameterValue]
    );

    const onCellEditCommit = React.useCallback(
        (params: GridCellEditCommitParams, event?: MuiBaseEvent) => {
            const { id, field, value } = params;
            const model = apiRef.current.getRowModels().get(id); // The current value of the row being edited...

            // If the value hasn't actually changed OR we haven't triggered this via a setFieldValue call, skip the below...
            if (_.isEqual(value, model[field]) && (event as Event)?.type !== "selectionchange") {
                return;
            }

            const editedRow = { ...model, [field]: value, editedFields: _.uniq([...(model?.editedFields || []), field]) }; // The data that will be committed
            if (onRowEdited) {
                const result = onRowEdited(editedRow, model);

                // Short-circuit if the 'onRowEdited' handler returned a falsey value...
                if (result !== undefined && !result) {
                    return;
                }
            }
            model.editedFields = editedRow.editedFields;

            if (editedRowsParameter) {
                const editedRows = parameterValues.get(editedRowsParameter) || [];
                // If this row was edited previously, exclude the old version from the changeset and only include the latest version...
                const editedRowsExcludingCurrentRow = editedRows.filter(r => r.id !== id);
                dispatch(updateParameterValue(editedRowsParameter, [...editedRowsExcludingCurrentRow, editedRow]));
            }

            if (allRowsParameter) {
                setTimeout(() => {
                    const gridRows = Array.from(apiRef.current.getRowModels().entries()).map(([_, item]) => item);
                    dispatch(updateParameterValue(allRowsParameter, gridRows));
                }, 50);
            }
        },
        [apiRef, parameterValues, updateParameterValue, onRowEdited]
    );

    return (
        <DataGrid
            apiRef={apiRef}
            columnBuffer={Number.MAX_SAFE_INTEGER}
            {...other}
            className={clsx("table-component", navigateTo && "navigate-on-click", className)}
            components={{
                Footer: GridFooter
            }}
            componentsProps={{
                footer: { hideExportToExcel: props.hideExportToExcel, additionalFooterItems }
            }}
            columns={allColumns}
            rows={currentRows || []}
            onCellEditCommit={onCellEditCommit}
            onRowClick={props.onRowClick || onRowClick}
        />
    );
}

(Table as DocumentedComponent).metadata = {
    description:
        `The \`Table\` component allows for the display of tabular data in a grid.  This is the preferred component for displaying tabular data.

        See [the base component's documentation](https://mui.com/components/data-grid/) for the full feature list.`,
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },

        { 
            name: "rows",
            type: "object",
            exactType: "{ [field: string]: any }[]",
            description: "The data to display in the grid.  Each row entry must be an object containing a superset of the fields defined in `columns`."
        },
        {
            name: "columns",
            type: "object",
            exactType: "{ [field: string]: { field: string, headerName: string, description?: string, type: 'string' | 'number' | 'date' | 'dateTime' | 'boolean', flex?: number, width?: number, minWidth?: number, editable?: boolean, renderCell?: (params: GridRenderCellParams) => React.ReactNode, renderEditCell?: (params: GridRenderEditCellParams) => React.ReactNode, renderTooltip?: (params: GridRenderCellParams) => React.ReactNode, valueFormatter?: (params: GridValueFormatterParams) => GridCellValue | Array<string> } }[]",
            description:
                `The columns to display in the grid.  See below for the definition of the \`Column\` type.
            
### \`Column\` Props:

| Name | Type | Description |
|------|------|-------------|
| \`field\` | \`string\` | The name of the field to display in this column.  Corresponds to a key in the row object. |
| \`headerName\` | \`string\` | The title of the column rendered in the column header cell. |
| \`description\` | \`string?\` | The tooltip to display when the user hovers over the column header. |
| \`type\` | \`'string' \\| 'number' \\| 'date' \\| 'dateTime' \\| 'boolean'\` | The type of data that is displayed in the column.  Determines how to display the data (alignment, formatting, controls) and which filters to expose in the data filter dialog. |
| \`flex\` | \`number?\` | If set, it indicates that a column has fluid width.  All columns with a \`flex\` value populated will share the available horizontal space according to this ratio.  Optional - defaults to \`null\`. |
| \`width\` | \`number?\` | The initial width of the column in \`px\`.  The \`flex\` property above takes precedence over this value, if populated.  Optional - defaults to \`100\`. |
| \`minWidth\` | \`number?\` | The minimum width of the column.  Applies to both calculated \`flex\` sizes and user interaction (column resizing).  Optional - defaults to \`50\`. |
| \`editable\` | \`boolean?\` | If \`true\`, the user can edit the cell data inline by double-clicking on the cell.  Optional - defaults to \`false\`. |
| \`cellClassName\` | \`string\` \| \`(params: GridCellParams) => string\` | The optional class name to apply to the cell.  Can be either a string (shared across all rows) or a callback function which returns the class name(s) for the current cell.  The callback function's input parameter has a field called \`value\`, containing the current cell's field value and one called \`row\`, containing the source data for the current row. |
| \`renderCell\` | \`(params: GridRenderCellParams) => ReactNode\` | The optional render function to use for rendering the cell body.  The input parameter has a field called \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. |
| \`renderEditCell\` | \`(params: GridRenderEditCellParams) => ReactNode\` | The optional render function to use for rendering the cell body when in edit mode.  The input parameter has a field called \`api\`, which can be used to stage the latest changes, \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. |
| \`renderTooltip\` | \`(params: GridRenderCellParams) => ReactNode\` | The optional render function to use for rendering the cell's tooltip.  The input parameter has a field called \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. |
| \`valueFormatter\` | \`(params: GridValueFormatterParams) => string\` | The optional formatter function to use for cell contents.  \`cellTemplate\` takes precedence over this field. |`
        },
        {
            name: "additionalColumns",
            type: "object",
            exactType: "{ [field: string]: { field: string, headerName: string, description?: string, type: 'string' | 'number' | 'date' | 'dateTime' | 'boolean', flex?: number, width?: number, minWidth?: number, editable?: boolean, renderCell?: (params: GridRenderCellParams) => React.ReactNode, renderEditCell?: (params: GridRenderEditCellParams) => React.ReactNode, renderTooltip?: (params: GridRenderCellParams) => React.ReactNode, valueFormatter?: (params: GridValueFormatterParams) => GridCellValue | Array<string> } }[]",
            description:
                `Additional columns to display in the grid.  This collection is appended to \`columns\` before rendering occurs.  This split allows for some columns to be dynamically defined in the data source and the rest to be defined in the template itself.
                
### \`Column\` Props:

| Name | Type | Description |
|------|------|-------------|
| \`field\` | \`string\` | The name of the field to display in this column.  Corresponds to a key in the row object. |
| \`headerName\` | \`string\` | The title of the column rendered in the column header cell. |
| \`description\` | \`string?\` | The tooltip to display when the user hovers over the column header. |
| \`type\` | \`'string' \\| 'number' \\| 'date' \\| 'dateTime' \\| 'boolean'\` | The type of data that is displayed in the column.  Determines how to display the data (alignment, formatting, controls) and which filters to expose in the data filter dialog. |
| \`flex\` | \`number?\` | If set, it indicates that a column has fluid width.  All columns with a \`flex\` value populated will share the available horizontal space according to this ratio.  Optional - defaults to \`null\`. |
| \`width\` | \`number?\` | The initial width of the column in \`px\`.  The \`flex\` property above takes precedence over this value, if populated.  Optional - defaults to \`100\`. |
| \`minWidth\` | \`number?\` | The minimum width of the column.  Applies to both calculated \`flex\` sizes and user interaction (column resizing).  Optional - defaults to \`50\`. |
| \`editable\` | \`boolean?\` | If \`true\`, the user can edit the cell data inline by double-clicking on the cell.  Optional - defaults to \`false\`. |
| \`cellClassName\` | \`string\` \| \`(params: GridCellParams) => string\` | The optional class name to apply to the cell.  Can be either a string (shared across all rows) or a callback function which returns the class name(s) for the current cell.  The callback function's input parameter has a field called \`value\`, containing the current cell's field value and one called \`row\`, containing the source data for the current row. |
| \`renderCell\` | \`(params: GridRenderCellParams) => ReactNode\` | The optional render function to use for rendering the cell body.  The input parameter has a field called \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. |
| \`renderEditCell\` | \`(params: GridRenderEditCellParams) => ReactNode\` | The optional render function to use for rendering the cell body when in edit mode.  The input parameter has a field called \`api\`, which can be used to stage the latest changes, \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. |
| \`renderTooltip\` | \`(params: GridRenderCellParams) => ReactNode\` | The optional render function to use for rendering the cell's tooltip.  The input parameter has a field called \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. |
| \`valueFormatter\` | \`(params: GridValueFormatterParams) => string\` | The optional formatter function to use for cell contents.  \`cellTemplate\` takes precedence over this field. |`
        },

        { name: "navigateTo", type: "string", description: "The URL to navigate to when the user clicks on a row.  Query parameters are appended to this URL using the `parameterMap` provided.  If not specified, no navigation occurs on row click." },
        { name: "parameterMap", type: "object", exactType: "{ [field: string]: string }", description: "The mapping of row fields to parameter values.  This is used when the `navigateTo` value is populated with a navigation URL and the user clicks on a row.  Query parameters are appended to the URL in the format: `<value>=rowValue[<key>]`." },

        { name: "selectedRowParameter", type: "string", description: "The name of the parameter to bind the currently selected row to.  The entire row is bound to this parameter and can be decomposed where the parameter is used." },
        { name: "allRowsParameter", type: "string", description: "The name of the parameter to bind the current grid rows to.  This will include both edited (latest version) and unedited rows." },
        { name: "editedRowsParameter", type: "string", description: "The name of the parameter to bind the edited grid rows to.  This parameter will be populated with the latest version of all rows that were edited by the user." },

        { name: "onRowClick", type: "function", exactType: "(e: MUI.GridRowParams) => void", template: `onRowClick={(e) => {const { row } = e; $1}}`, description: "The callback function to execute when a grid row is clicked on.  The sole input parameter contains a field called `row`, which can be used to access the row data." },
        { name: "onRowEdited", type: "function", exactType: "(row: any, originalRow: any) => void | boolean", template: `onRowEdited={(row, originalRow) => $1}}`, description: "The callback function to execute after a grid row/cell has been edited.  The first input parameter is the row data after the update has occurred.  The second input parameter is the row data just before the update has been applied.  If a \`falsey\` value is returned, the edit operation is cancelled and the changes are reverted." },

        { name: "hideFooter", type: "boolean", description: "Set this property to `true` in order to hide the table footer.  Defaults to `false`." },
        { name: "hideExportToExcel", type: "boolean", description: "Set this property to `true` in order to hide the \"Export to Excel\" button in the table footer.  Defaults to `false`." },
        { name: "additionalFooterItems", type: "object", exactType: "React.ReactNode", description: "Additional items to display in the table footer.  If provided, these items are displayed alonside the \"Export to Excel\" button." },

        { name: "density", type: "string", exactType: "'comfortable' | 'compact' | 'standard'", options: ["comfortable", "compact", "standard"], description: "Determines the heading and row height for the table.  Defaults to `\"standard\"``." },
    ]
};

export default Table;